import React, {useMemo, useState} from "react";
import FfairModal from "view/components/modal/FfairModal";
import FfairPaper from "view/components/FfairPaper";
import Form, {ActionButton, Actions} from "view/components/Form";
import {useForm} from "react-hook-form";
import FlexBox from "view/components/Layout/FlexBox";
import {ITerm, ITermToBuilder} from "@evennt-tech/api-v2-core";
import CheckboxGroup, {CheckboxGroupItem} from "view/components/CheckboxGroup";
import {useCurrentShowBuildTypes, useCurrentShowId} from "hooks";
import {CheckboxProps} from "@material-ui/core";
import {apiV2} from "api/firebase";
import {
  AssignTermPopupConfirmationModal,
  AssignTermPopupConfirmationModalState,
  AssignTermPopupFormReturn,
} from "view/apps/Organiser/pages/Show/Glossary/components/AssignTermPopupToBuildTypeModal/utils/types";
import {useConflictingTermPopupBuildTypes} from "view/apps/Organiser/pages/Show/Glossary/components/AssignTermPopupToBuildTypeModal/utils/useConflictingTermPopupBuildTypes";
import {useTermPopupConfirmationModal} from "view/apps/Organiser/pages/Show/Glossary/components/AssignTermPopupToBuildTypeModal/utils/useTermPopupConfirmationModal";
import TermPopupWarning from "view/apps/Organiser/pages/Show/Glossary/components/AssignTermPopupToBuildTypeModal/components/TermPopupWarning";
import {getItemById} from "tools/common/getItemById";
import useStyles from "./styles";

type Props = {
  onClose: () => void;
  terms: ITerm[];
  popupTerm: ITerm;
};

const AssignTermPopupToBuildTypeModal = ({
  onClose,
  terms,
  popupTerm,
}: Props) => {
  const classes = useStyles();

  const showId = useCurrentShowId();

  const [builders, isBuildersLoading] = useCurrentShowBuildTypes();

  const termsByBuilder = useMemo(
    () =>
      terms.reduce<Map<string, ITermToBuilder[]>>((acc, {termToBuilders}) => {
        termToBuilders?.forEach((termToBuilder) => {
          const currentBuilderTerms = acc.get(termToBuilder.builderId) ?? [];
          currentBuilderTerms.push(termToBuilder);
          acc.set(termToBuilder.builderId, currentBuilderTerms);
        });
        return acc;
      }, new Map()),
    [terms]
  );

  const termById = useMemo(() => getItemById(terms), [terms]);
  const [defaultBuilderIds] = useState(() =>
    builders.reduce<string[]>((acc, curr) => {
      const currentBuilderTerms = termsByBuilder.get(curr.id);
      if (!currentBuilderTerms?.length) {
        return acc;
      }
      const hasBuilderPopupEnabled = currentBuilderTerms.some(
        (ttb) => ttb.shouldShowPopup && ttb.termId === popupTerm.id
      );
      if (hasBuilderPopupEnabled) {
        acc.push(curr.id);
      }

      return acc;
    }, [])
  );

  const form = useForm<AssignTermPopupFormReturn>({
    defaultValues: {
      selectedBuilderIds: defaultBuilderIds,
    },
  });
  const {watch, handleSubmit, setValue} = form;
  const selectedBuilderIds = watch("selectedBuilderIds");

  const onSubmit = async (newSelectedBuilderIds: string[]) => {
    await apiV2.glossary.category.term.assignPopupTermToBuilders({
      termId: popupTerm.id,
      builderIds: newSelectedBuilderIds,
      showId,
    });

    if (newSelectedBuilderIds.length) {
      await apiV2.exhibitor.userConnection.updateShowPopups({
        showId,
        builderIds: newSelectedBuilderIds,
      });
    }
    onClose();
  };

  const {checkConflictingBuilders} = useConflictingTermPopupBuildTypes({
    termById,
    popupTerm,
    termsByBuilder,
    builders,
    selectedBuilderIds,
  });

  const {
    confirmationModalView,
    setConfirmationModals,
  } = useTermPopupConfirmationModal(builders, form, termById, onSubmit);

  const allOptions = useMemo<CheckboxGroupItem[]>(() => {
    if (!builders.length) {
      return [];
    }
    return builders.map((builder) => {
      const conflictingBuilderId = checkConflictingBuilders([builder.id]);
      const label = `${builder.name}${
        conflictingBuilderId ? " (Pop-up assigned)" : ""
      }`;

      return {
        value: builder.id,
        label,
      };
    });
  }, [builders, checkConflictingBuilders]);

  const onTryToSubmit = async ({
    selectedBuilderIds: newSelectedBuilderIds,
  }: AssignTermPopupFormReturn) => {
    const newConfirmationModals: AssignTermPopupConfirmationModalState[] = [];

    const missingTermBuilders = newSelectedBuilderIds.filter(
      (bId) => !popupTerm.termToBuilders?.some((ttb) => ttb.builderId === bId)
    );
    if (missingTermBuilders.length) {
      newConfirmationModals.push({
        type: AssignTermPopupConfirmationModal.AssignBuilders,
        builderIds: missingTermBuilders,
      });
    }

    const conflictingTermByBuilder = checkConflictingBuilders(
      newSelectedBuilderIds
    );
    if (conflictingTermByBuilder) {
      newConfirmationModals.push({
        type: AssignTermPopupConfirmationModal.Unassign,
        conflictingTermByBuilder,
      });
    }

    if (newConfirmationModals.length) {
      return setConfirmationModals(newConfirmationModals);
    }

    await onSubmit(newSelectedBuilderIds);
  };

  const setSelectedBuilderIds = (values: string[]) => {
    setValue("selectedBuilderIds", values, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleSingleBuilderChange = (builderId: string, checked: boolean) => {
    setSelectedBuilderIds(
      checked
        ? [...selectedBuilderIds, builderId]
        : selectedBuilderIds.filter((bId) => bId !== builderId)
    );
  };

  const handleAllBuilderChange = (checked: boolean) => {
    setSelectedBuilderIds(checked ? builders.map((b) => b.id) : []);
  };

  const onChange: CheckboxProps["onChange"] = ({target}, checked) => {
    const {name} = target;
    if (name === "all") {
      handleAllBuilderChange(checked);
    } else {
      handleSingleBuilderChange(name, checked);
    }
  };

  const isBuildersEmpty = !isBuildersLoading && !builders?.length;
  return (
    <>
      {confirmationModalView}
      <FfairModal open onClose={onClose}>
        <FfairPaper className={classes.root}>
          <Form
            subTitle="Select a Build Type"
            title="Pop-up"
            noDefaultStyles
            className={classes.form}
            classes={{title: classes.title, subtitle: classes.subtitle}}
            form={form}
            onSubmit={handleSubmit(onTryToSubmit)}
          >
            <FlexBox gap={2} column>
              {!isBuildersLoading && !!builders?.length && (
                <CheckboxGroup
                  name="selectedBuilderIds"
                  items={allOptions}
                  includeAll
                  includeAllLabel="All"
                  onChange={onChange}
                  required={false}
                />
              )}
              <TermPopupWarning isBuildersEmpty={isBuildersEmpty} />
              <Actions className={classes.actions}>
                <ActionButton
                  text={isBuildersEmpty ? "Close" : "Cancel"}
                  onClick={onClose}
                />
                {!isBuildersEmpty && (
                  <ActionButton
                    text="Submit"
                    type="submit"
                    disabled={
                      !defaultBuilderIds.length && !selectedBuilderIds.length
                    }
                  />
                )}
              </Actions>
            </FlexBox>
          </Form>
        </FfairPaper>
      </FfairModal>
    </>
  );
};

export default AssignTermPopupToBuildTypeModal;
